import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { searchAppVersion, deleteAppVersion, updateAppVersion } from '../../../constant/Constant';
import { Plus, Trash2, Pencil, ArrowUpCircle, AlertTriangle, CheckCircle, Smartphone, Apple, Globe } from 'lucide-react';
import toast from 'react-hot-toast';

const VersionMaster = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  useEffect(() => {
    fetchRecords();
  }, [page]);

  const fetchRecords = () => {
    setLoading(true);
    HitApi({ page, limit, sortBy: 'buildNumber', sortOrder: 'desc' }, searchAppVersion)
      .then((res) => {
        if (res?.data?.docs) {
          setRecords(res.data.docs);
          setTotalPages(res.data.totalPages || 1);
        }
      })
      .catch(() => toast.error('Failed to fetch versions'))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!deleteModal.id) return;
    HitApi({ _id: deleteModal.id }, deleteAppVersion)
      .then((res) => {
        if (res?.success) {
          toast.success('Version deleted');
          fetchRecords();
        } else {
          toast.error(res?.message || 'Failed to delete');
        }
      })
      .catch(() => toast.error('Failed to delete'))
      .finally(() => setDeleteModal({ open: false, id: null }));
  };

  const toggleActive = (record) => {
    HitApi({ _id: record._id, isActive: !record.isActive }, updateAppVersion)
      .then((res) => {
        if (res?.success) {
          toast.success(`Version ${!record.isActive ? 'activated' : 'deactivated'}`);
          fetchRecords();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      })
      .catch(() => toast.error('Failed to update'));
  };

  const toggleForceUpdate = (record) => {
    HitApi({ _id: record._id, forceUpdate: !record.forceUpdate }, updateAppVersion)
      .then((res) => {
        if (res?.success) {
          toast.success(`Force update ${!record.forceUpdate ? 'enabled' : 'disabled'}`);
          fetchRecords();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      })
      .catch(() => toast.error('Failed to update'));
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'android') return <Smartphone className="w-3.5 h-3.5" />;
    if (platform === 'ios') return <Apple className="w-3.5 h-3.5" />;
    return <Globe className="w-3.5 h-3.5" />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Version Control</h1>
          <p className="text-sm text-gray-500 mt-1">Manage app releases and force update policies</p>
        </div>
        <button
          onClick={() => navigate('/app-control/versions/add')}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Release
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      )}

      {!loading && records.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-100">
          <ArrowUpCircle className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-medium">No app versions configured</p>
          <p className="text-gray-400 text-xs mt-1">Add your first release to enable version control</p>
        </div>
      )}

      {!loading && records.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Version</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Build</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Platform</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Release Type</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Force Update</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Refresh Data</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Released</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((record, idx) => (
                  <tr key={record._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm">v{record.version}</span>
                        {idx === 0 && (
                          <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">LATEST</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">#{record.buildNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${
                        record.platform === 'android' ? 'bg-green-50 text-green-700' :
                        record.platform === 'ios' ? 'bg-gray-100 text-gray-700' :
                        'bg-blue-50 text-blue-700'
                      }`}>
                        {getPlatformIcon(record.platform)}
                        {record.platform === 'both' ? 'All' : record.platform === 'ios' ? 'iOS' : 'Android'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        record.releaseType === 'important'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {record.releaseType === 'important' && <AlertTriangle className="w-3 h-3" />}
                        {record.releaseType === 'important' ? 'Important' : 'Normal'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleForceUpdate(record)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          record.forceUpdate ? 'bg-red-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                          record.forceUpdate ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          HitApi({ _id: record._id, refreshClientData: !record.refreshClientData }, updateAppVersion)
                            .then((res) => {
                              if (res?.success) {
                                toast.success(`Client refresh ${!record.refreshClientData ? 'enabled' : 'disabled'}`);
                                fetchRecords();
                              } else {
                                toast.error(res?.message || 'Failed to update');
                              }
                            })
                            .catch(() => toast.error('Failed to update'));
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          record.refreshClientData ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                          record.refreshClientData ? 'translate-x-[18px]' : 'translate-x-[3px]'
                        }`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(record)}
                        className={`text-xs px-2 py-1 rounded-full font-medium cursor-pointer ${
                          record.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {record.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-xs text-gray-600">{formatDate(record.releasedAt)}</span>
                        {record.releasedBy && (
                          <p className="text-[10px] text-gray-400 mt-0.5">by {record.releasedBy}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/app-control/versions/edit/${record._id}`)}
                          className="p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <Pencil className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, id: record._id })}
                          className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Version</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionMaster;
